import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Divider,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import HttpHelper from '../../utils/HttpHelper';

export interface OnboardingChecklist {
  welcomeEmailSent: boolean;
  personalInfoReceived: boolean;
  sharedGeneralOnboarding: boolean;
  sharedDepartmentOnboarding: boolean;
  addedToGitHub: boolean;
  addedToSignal: boolean;
  ttgEmailCreated: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  applicantId: number;
  applicantName: string;
}

const checklistItems: { key: keyof OnboardingChecklist; label: string }[] = [
  { key: 'welcomeEmailSent',           label: 'Welcome email sent' },
  { key: 'personalInfoReceived',       label: 'Personal Information received' },
  { key: 'sharedGeneralOnboarding',   label: 'Shared General onboarding' },
  { key: 'sharedDepartmentOnboarding',label: 'Shared department specific onboarding' },
  { key: 'addedToGitHub',             label: 'Added to GitHub' },
  { key: 'addedToSignal',             label: 'Added to Signal' },
  { key: 'ttgEmailCreated',           label: 'TTG email created' },
];

const defaultChecklist: OnboardingChecklist = {
  welcomeEmailSent: false,
  personalInfoReceived: false,
  sharedGeneralOnboarding: false,
  sharedDepartmentOnboarding: false,
  addedToGitHub: false,
  addedToSignal: false,
  ttgEmailCreated: false,
};

const OnboardingModal: React.FC<Props> = ({ open, onClose, applicantId, applicantName }) => {
  const [checklist, setChecklist] = useState<OnboardingChecklist>(defaultChecklist);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!open || !applicantId) return;
    setLoading(true);
    setNotFound(false);
    HttpHelper.get(`onboarding/applicant/${applicantId}`).then(res => {
      setLoading(false);
      if (res.errorMessage) {
        setNotFound(true);
      } else if (res.onboarding) {
        const r = res.onboarding;
        setChecklist({
          welcomeEmailSent:           r.welcomeEmailSent ?? false,
          personalInfoReceived:       r.personalInfoReceived ?? false,
          sharedGeneralOnboarding:   r.sharedGeneralOnboarding ?? false,
          sharedDepartmentOnboarding: r.sharedDepartmentOnboarding ?? false,
          addedToGitHub:             r.addedToGitHub ?? false,
          addedToSignal:             r.addedToSignal ?? false,
          ttgEmailCreated:           r.ttgEmailCreated ?? false,
        });
      }
    });
  }, [open, applicantId]);

  const handleToggle = (key: keyof OnboardingChecklist) => {
    setChecklist(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    const res = await HttpHelper.put(`onboarding/applicant/${applicantId}`, checklist);
    setSaving(false);
    if (res.errorMessage) {
      alert(res.errorMessage);
    } else {
      onClose();
    }
  };

  const completedCount = Object.values(checklist).filter(Boolean).length;
  const totalCount = checklistItems.length;
  const progressPct = Math.round((completedCount / totalCount) * 100);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Onboarding Checklist
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {applicantName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Progress bar */}
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="caption" fontWeight={700} color={progressPct === 100 ? 'success.main' : 'text.primary'}>
              {completedCount}/{totalCount} complete
            </Typography>
          </Box>
          <Box
            sx={{
              width: '100%',
              height: 6,
              borderRadius: 3,
              backgroundColor: '#e0e0e0',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${progressPct}%`,
                height: '100%',
                borderRadius: 3,
                backgroundColor: progressPct === 100 ? '#2e7d32' : '#1976d2',
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : notFound ? (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 3 }}>
            No onboarding record found. This candidate may not have been marked as Accepted yet.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {checklistItems.map(({ key, label }) => (
              <Box
                key={key}
                onClick={() => handleToggle(key)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  backgroundColor: checklist[key] ? 'rgba(46,125,50,0.06)' : 'transparent',
                  '&:hover': { backgroundColor: checklist[key] ? 'rgba(46,125,50,0.1)' : 'rgba(0,0,0,0.04)' },
                }}
              >
                {checklist[key] ? (
                  <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 22, flexShrink: 0 }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ color: '#bbb', fontSize: 22, flexShrink: 0 }} />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: checklist[key] ? 'line-through' : 'none',
                    color: checklist[key] ? 'text.secondary' : 'text.primary',
                    fontWeight: checklist[key] ? 400 : 500,
                    transition: 'all 0.15s',
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || notFound}
          sx={{
            textTransform: 'none',
            backgroundColor: '#2e7d32',
            '&:hover': { backgroundColor: '#1b5e20' },
            minWidth: 100,
          }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingModal;
